// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import type MapView from '@arcgis/core/views/MapView';
import type SceneView from '@arcgis/core/views/SceneView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

// How long a selection's highlight stays before it clears on its own.
const HIGHLIGHT_FLASH_MS = 4000;

// AirWorks Bright Blue (#01A1DD), drawn ON TOP so every selected feature is visible even when its
// source layer is occluded by a denser layer (e.g. ROAD_LINE_CLEARANCE points hidden under the 67
// LOWEST_LINE_SAG points), which native layerView.highlight — it only tints the existing symbol —
// can't overcome.
const HILITE = [1, 161, 221];

const markerSymbol = new SimpleMarkerSymbol({
  style: 'circle',
  size: 13,
  color: [...HILITE, 0.9],
  outline: { color: [255, 255, 255, 1], width: 2 },
});
const lineSymbol = new SimpleLineSymbol({ color: [...HILITE, 1], width: 4 });
const fillSymbol = new SimpleFillSymbol({
  color: [...HILITE, 0.25],
  outline: { color: [...HILITE, 1], width: 3 },
});

const symbolFor = (geometry: __esri.Geometry): SimpleMarkerSymbol | SimpleLineSymbol | SimpleFillSymbol => {
  if (geometry.type === 'polyline') return lineSymbol;
  if (geometry.type === 'polygon' || geometry.type === 'extent') return fillSymbol;
  return markerSymbol; // point / multipoint / anything else
};

// The CURRENT selection's native highlight handles + the bright overlay layer. Both are cleared the
// moment the next selection arrives (and after the flash window) so highlights never pile up.
let activeHandles: __esri.Handle[] = [];
let activeOverlay: GraphicsLayer | null = null;

const clearActive = (view?: MapView | SceneView): void => {
  activeHandles.forEach((handle) => handle.remove());
  activeHandles = [];
  if (activeOverlay) {
    try {
      view?.map?.remove(activeOverlay);
      activeOverlay.destroy();
    } catch {
      // already removed/destroyed
    }
    activeOverlay = null;
  }
};

/**
 * Highlight `groups` of features on the Esri view (a brief flash). Highlight ONLY — no goTo/zoom:
 * panning the camera would change the extent and, in "current view only" mode, recompute the
 * dashboard; the user drives the map area instead. Each group is a layer + its features to highlight.
 * Uses native layerView.highlight AND a bright overlay so occluded features still show.
 */
const highlightAndFlash = async (
  view: MapView | SceneView,
  groups: { layer: __esri.Layer; features: __esri.Graphic[] }[],
): Promise<void> => {
  clearActive(view); // never let highlights accumulate

  const allFeatures = groups.flatMap((group) => group.features);
  if (!view || !allFeatures.length) return;

  // 1) Native layerView highlight (a glow on the source symbol) where the layer view is ready.
  const handles: __esri.Handle[] = [];
  await Promise.all(groups.map(async (group) => {
    try {
      const layerView = await view.whenLayerView(group.layer) as __esri.FeatureLayerView;
      handles.push(layerView.highlight(group.features));
    } catch {
      // Layer view not ready or not highlightable — the overlay below still shows it.
    }
  }));
  activeHandles = handles;

  // 2) Bright overlay markers ON TOP — guarantees every selected feature is visible regardless of
  //    source-layer rendering or occlusion by denser layers.
  const overlay = new GraphicsLayer({
    title: '__dashboard_highlight',
    listMode: 'hide',
    elevationInfo: { mode: 'on-the-ground' },
  });
  allFeatures.forEach((feature) => {
    if (feature.geometry) overlay.add(new Graphic({ geometry: feature.geometry, symbol: symbolFor(feature.geometry) }));
  });
  view.map.add(overlay);
  activeOverlay = overlay;

  window.setTimeout(() => {
    handles.forEach((handle) => handle.remove());
    try {
      view.map.remove(overlay);
      overlay.destroy();
    } catch {
      // already gone
    }
    if (activeHandles === handles) activeHandles = [];
    if (activeOverlay === overlay) activeOverlay = null;
  }, HIGHLIGHT_FLASH_MS);
};

export default highlightAndFlash;
